/**
 * FabAmhsLifter3D.tsx
 *
 * Centralized Generic AMHS Vertical Lifter Component for:
 * 1. Stocker entry/exit load ports (Stocker3D, StockerExternalLoadPort, StockerLoadPortBank).
 * 2. Standalone Entry / Exit load ports (LoadPort, STB, OHB).
 * 3. Process Equipment load ports (CleanroomWorkcell, NewEquipment).
 *
 * Visually and mechanically connects from lower deck (below) all the way up to overhead track level (above):
 * - 4 structural silver-anodized aluminum vertical columns.
 * - Dual vertical synchronous timing drive belts (Blue on left, Green on right).
 * - Dual full-height polished chrome linear guide shafts.
 * - Intermediate structural cross-tie ladder tiers with optical sensor gates.
 * - Top cantilever track outrigger frame with saddle clamping brackets connecting directly under the rail.
 * - Moving lifter carriage platform with SEMI kinematic pins and status LED strip.
 * - Realistic animated FOUP traveling vertically through the lifter with smootherstep kinematics.
 */
import React, { useMemo } from 'react';
import * as THREE from 'three';
import { useRailElevation } from '../../railElevationContext';
import { FOUP } from '../FOUP/FOUP';
import { resolveKitFoupScale } from '../types';
import {
  OHT_TRACK_DROP_RELEASE_PROGRESS,
  OHT_TRACK_PICKUP_TAKE_PROGRESS,
} from '../../../../features/simulation/ohtCarriedFoupVisible';

export type FabAmhsLifter3DProps = {
  id?: string;
  position?: [number, number, number];
  bottomY?: number; // Lower deck / floor elevation (default ~ 1.2 to 2.5)
  topTrackY?: number; // Overhead track rail elevation (default reads from useRailElevation)
  width?: number; // Lifter shaft width (default ~ 3.6 to 4.2)
  depth?: number; // Lifter shaft depth (default ~ 3.6 to 4.2)
  extendDistance?: number; // Horizontal shuttle tray reach to track
  extendDirection?: 1 | -1; // Direction along Z toward track (-1 for -Z, 1 for +Z)
  isTransferring?: boolean;
  progress01?: number; // 0..1 transfer progress
  transferOperation?: 'PICKUP' | 'DROP' | 'TRANSFER';
  foupPresent?: boolean;
  status?: string;
  opacity?: number;
  active?: boolean;
  showTrackClamp?: boolean;
  showOuterFrame?: boolean;
  showFoup?: boolean;
  /** Normalized progress when OHT releases (DROP) or takes (PICKUP) the FOUP at track. */
  ohtDropReleaseProgress?: number;
  ohtPickupTakeProgress?: number;
  onSelect?: () => void;
};

export const FabAmhsLifter3D: React.FC<FabAmhsLifter3DProps> = ({
  id = 'LIFTER-01',
  position = [0, 0, 0],
  bottomY = 1.2,
  topTrackY,
  width = 3.8,
  depth = 3.8,
  extendDistance,
  extendDirection = -1,
  isTransferring = false,
  progress01 = 0,
  transferOperation = 'PICKUP',
  foupPresent = false,
  status = 'IDLE',
  opacity = 1,
  active = false,
  showTrackClamp = true,
  showOuterFrame = true,
  showFoup = true,
  ohtDropReleaseProgress = OHT_TRACK_DROP_RELEASE_PROGRESS,
  ohtPickupTakeProgress = OHT_TRACK_PICKUP_TAKE_PROGRESS,
  onSelect,
}) => {
  void status;
  void showTrackClamp;
  const dynamicRailY = useRailElevation();
  const effectiveTrackY = topTrackY ?? (Math.max(28.0, dynamicRailY ?? 32.0) + 1.2);

  const shaftHeight = effectiveTrackY - bottomY;
  const shaftMidY = bottomY + shaftHeight * 0.5;
  const midShelfY = bottomY + shaftHeight * 0.48;

  const halfW = width * 0.5;
  const halfD = depth * 0.5;
  const postSize = 0.26;

  // Materials
  const mats = useMemo(() => {
    return {
      silverAluminum: new THREE.MeshStandardMaterial({
        color: '#E2E8F0',
        metalness: 0.88,
        roughness: 0.18,
        envMapIntensity: 1.5,
        transparent: opacity < 1,
        opacity,
      }),
      darkChassis: new THREE.MeshStandardMaterial({
        color: '#1E293B',
        metalness: 0.45,
        roughness: 0.32,
        transparent: opacity < 1,
        opacity,
      }),
      chromeShaft: new THREE.MeshStandardMaterial({
        color: '#F8FAFC',
        metalness: 0.98,
        roughness: 0.08,
        envMapIntensity: 2.2,
      }),
      timingBeltBlue: new THREE.MeshStandardMaterial({
        color: '#0284C7',
        roughness: 0.4,
        metalness: 0.2,
      }),
      timingBeltGreen: new THREE.MeshStandardMaterial({
        color: '#10B981',
        roughness: 0.4,
        metalness: 0.2,
      }),
      carriagePlate: new THREE.MeshStandardMaterial({
        color: '#CBD5E1',
        metalness: 0.85,
        roughness: 0.2,
      }),
      safetyYellow: new THREE.MeshStandardMaterial({
        color: '#EAB308',
        metalness: 0.28,
        roughness: 0.32,
      }),
      clearGlass: new THREE.MeshPhysicalMaterial({
        color: '#38BDF8',
        transmission: 0.88,
        opacity: 0.38,
        transparent: true,
        roughness: 0.08,
        metalness: 0.12,
        ior: 1.5,
      }),
      smokedGlass: new THREE.MeshPhysicalMaterial({
        color: '#0F172A',
        transmission: 0.82,
        opacity: 0.45,
        transparent: true,
        roughness: 0.12,
        metalness: 0.18,
        ior: 1.5,
      }),
    };
  }, [opacity]);

  // Motion Kinematics: Vertical carriage stroke + Horizontal Top Shuttle Tray extension
  const carriageMotion = useMemo(() => {
    const TOP_Y = effectiveTrackY + 0.18;
    const BOT_Y = bottomY + 0.15;
    const stroke = TOP_Y - BOT_Y;
    const reach = extendDistance ?? Math.max(1.8, depth * 0.55);
    const extendDist = reach * extendDirection;

    let carriageY = BOT_Y;
    let shuttleZ = 0;
    let foupVisible = foupPresent;

    if (isTransferring) {
      const p = Math.max(0, Math.min(1, progress01));

      if (transferOperation === 'PICKUP') {
        // 1. Carriage ascends with FOUP from lower deck to top (0.00..0.40)
        // 2. Carriage tray extends horizontally to track stop node under OHT (0.40..0.70)
        // 3. OHT receives FOUP at 0.70..0.80 (lifter tray holds at track node)
        // 4. Empty tray retracts back into carriage (0.80..0.88)
        // 5. Empty carriage & tray descends with animation back down to lower deck (0.88..1.00)
        if (p < 0.40) {
          const u = p / 0.40;
          const smooth = u * u * u * (u * (u * 6 - 15) + 10);
          carriageY = BOT_Y + smooth * stroke;
          shuttleZ = 0;
          foupVisible = true;
        } else if (p + 1e-6 < ohtPickupTakeProgress) {
          carriageY = TOP_Y;
          const u = (p - 0.40) / Math.max(1e-6, ohtPickupTakeProgress - 0.4);
          const smooth = u * u * u * (u * (u * 6 - 15) + 10);
          shuttleZ = smooth * extendDist;
          foupVisible = true;
        } else if (p < 0.80) {
          carriageY = TOP_Y;
          shuttleZ = extendDist;
          foupVisible = false;
        } else if (p < 0.88) {
          carriageY = TOP_Y;
          const u = (p - 0.80) / 0.08;
          const smooth = u * u * u * (u * (u * 6 - 15) + 10);
          shuttleZ = (1 - smooth) * extendDist;
          foupVisible = false;
        } else {
          shuttleZ = 0;
          const u = (p - 0.88) / 0.12;
          const smooth = u * u * u * (u * (u * 6 - 15) + 10);
          carriageY = TOP_Y - smooth * stroke;
          foupVisible = false;
        }
      } else if (transferOperation === 'DROP') {
        // 1. Carriage at top, empty tray extends to track stop node under OHT (0.00..0.15)
        // 2. Receives FOUP from OHT at 0.15; tray retracts into carriage with FOUP (0.15..0.45)
        // 3. Vertical Carriage descends with FOUP to lower deck (0.45..0.88)
        // 4. Rests securely at lower deck (0.88..1.00)
        if (p + 1e-6 < ohtDropReleaseProgress) {
          const u = p / Math.max(1e-6, ohtDropReleaseProgress);
          const smooth = u * u * u * (u * (u * 6 - 15) + 10);
          shuttleZ = smooth * extendDist;
          carriageY = TOP_Y;
          foupVisible = false;
        } else if (p < 0.45) {
          const u =
            (p - ohtDropReleaseProgress) /
            Math.max(1e-6, 0.45 - ohtDropReleaseProgress);
          const smooth = u * u * u * (u * (u * 6 - 15) + 10);
          shuttleZ = (1 - smooth) * extendDist;
          carriageY = TOP_Y;
          foupVisible = true;
        } else if (p < 0.88) {
          const u = (p - 0.45) / 0.43;
          const smooth = u * u * u * (u * (u * 6 - 15) + 10);
          carriageY = TOP_Y - smooth * stroke;
          shuttleZ = 0;
          foupVisible = true;
        } else {
          carriageY = BOT_Y;
          shuttleZ = 0;
          foupVisible = true;
        }
      } else {
        const cycle = Math.sin(p * Math.PI * 2) * 0.5 + 0.5;
        carriageY = BOT_Y + cycle * stroke;
        shuttleZ = 0;
        foupVisible = true;
      }
    } else if (active) {
      carriageY = BOT_Y;
      shuttleZ = 0;
      foupVisible = foupPresent;
    }

    return { carriageY, shuttleZ, foupVisible };
  }, [
    bottomY,
    depth,
    effectiveTrackY,
    extendDistance,
    extendDirection,
    isTransferring,
    progress01,
    transferOperation,
    foupPresent,
    active,
    ohtDropReleaseProgress,
    ohtPickupTakeProgress,
  ]);

  const foupScale = resolveKitFoupScale(width, shaftHeight, depth);
  const activeColor = active || isTransferring ? '#00F0FF' : (foupPresent ? '#10B981' : '#475569');

  return (
    <group
      name={`FabAmhsLifter-${id}`}
      position={position}
      onClick={onSelect ? (e) => { e.stopPropagation(); onSelect(); } : undefined}
    >
      {showOuterFrame && (
        <>
          {/* ── 1. Lower Deck Platform Base (Y = bottomY) ── */}
          <mesh position={[0, bottomY - 0.1, 0]} material={mats.silverAluminum} castShadow receiveShadow>
            <boxGeometry args={[width + 0.3, 0.2, depth + 0.3]} />
          </mesh>
          <mesh position={[0, bottomY - 0.22, 0]} material={mats.darkChassis} receiveShadow>
            <boxGeometry args={[width + 0.5, 0.15, depth + 0.5]} />
          </mesh>

          {/* ── 2. Full-Height 4 Corner Aluminum Vertical Columns (Floor to Track) ── */}
          {[-1, 1].flatMap((sx) =>
            [-1, 1].map((sz) => (
              <mesh
                key={`lifter-col-${sx}-${sz}`}
                position={[sx * (halfW - postSize * 0.5), shaftMidY, sz * (halfD - postSize * 0.5)]}
                material={mats.silverAluminum}
                castShadow
                receiveShadow
              >
                <boxGeometry args={[postSize, shaftHeight, postSize]} />
              </mesh>
            ))
          )}

          {/* ── Transparent Light-Blue & Smoked Black Glass Lifter Chamber Windows ── */}
          {[-1, 1].map((sx) => (
            <mesh
              key={`lifter-glass-x-${sx}`}
              position={[sx * (halfW - 0.02), shaftMidY, 0]}
              material={mats.clearGlass}
            >
              <boxGeometry args={[0.03, shaftHeight - 0.2, depth - postSize * 2]} />
            </mesh>
          ))}
          {/* Front Face (+Z): Light Blue Glass */}
          <mesh
            position={[0, shaftMidY, halfD - 0.02]}
            material={mats.clearGlass}
          >
            <boxGeometry args={[width - postSize * 2, shaftHeight - 0.2, 0.03]} />
          </mesh>
          {/* Rear Face (-Z): Smoked Black Glass */}
          <mesh
            position={[0, shaftMidY, -halfD + 0.02]}
            material={mats.smokedGlass}
          >
            <boxGeometry args={[width - postSize * 2, shaftHeight - 0.2, 0.03]} />
          </mesh>

          {/* ── 5. Multi-Tier Structural Cross-Tie Braces Along Vertical Shaft ── */}
          {[bottomY + shaftHeight * 0.32, bottomY + shaftHeight * 0.68].map((by, bIdx) => (
            <group key={`cross-tie-${bIdx}`} position={[0, by, 0]}>
              <mesh position={[0, 0, -halfD + postSize * 0.5]} material={mats.silverAluminum}>
                <boxGeometry args={[width, postSize, postSize]} />
              </mesh>
              <mesh position={[0, 0, halfD - postSize * 0.5]} material={mats.silverAluminum}>
                <boxGeometry args={[width, postSize, postSize]} />
              </mesh>
            </group>
          ))}

          {/* ── 6. Intermediate Mechanical Stop Shelf (Transfer Stage Buffer) ── */}
          <group position={[0, midShelfY, 0]}>
            {/* Perimeter Frame Collar */}
            <mesh position={[0, 0, -halfD + postSize * 0.5]} material={mats.darkChassis}>
              <boxGeometry args={[width, 0.2, postSize]} />
            </mesh>
            <mesh position={[0, 0, halfD - postSize * 0.5]} material={mats.darkChassis}>
              <boxGeometry args={[width, 0.2, postSize]} />
            </mesh>
            <mesh position={[-halfW + postSize * 0.5, 0, 0]} material={mats.darkChassis}>
              <boxGeometry args={[postSize, 0.2, depth]} />
            </mesh>
            <mesh position={[halfW - postSize * 0.5, 0, 0]} material={mats.darkChassis}>
              <boxGeometry args={[postSize, 0.2, depth]} />
            </mesh>
            {/* Optical Sensor Indicators */}
            {[-1, 1].map((sx) => (
              <mesh key={`sensor-${sx}`} position={[sx * (halfW - 0.2), 0.18, 0]}>
                <cylinderGeometry args={[0.06, 0.06, 0.22, 12]} />
                <meshStandardMaterial color={activeColor} emissive={activeColor} emissiveIntensity={0.9} />
              </mesh>
            ))}
          </group>
        </>
      )}

      {/* ── 7. Single Unified Moving Lifter Carriage & Telescopic Shuttle Tray Nest ── */}
      <group position={[0, carriageMotion.carriageY, 0]} name="MovingLifterCarriage">
        {/* Main Carriage Base Platform */}
        <mesh material={mats.carriagePlate} castShadow receiveShadow>
          <boxGeometry args={[width * 0.84, 0.16, depth * 0.84]} />
        </mesh>
        {/* Lower Structural Cross Ribs */}
        <mesh position={[0, -0.12, 0]} material={mats.darkChassis} castShadow>
          <boxGeometry args={[width * 0.74, 0.2, depth * 0.74]} />
        </mesh>
        {/* Linear Bearing Guide Sleeves gripping vertical guide shafts */}
        {[-1, 1].map((sx) => (
          <mesh key={`bearing-${sx}`} position={[sx * (halfW - 0.15), 0, 0]} material={mats.darkChassis}>
            <boxGeometry args={[0.26, 0.38, 0.28]} />
          </mesh>
        ))}

        {/* Horizontal Telescopic Slide Rails on Carriage */}
        {[-1, 1].map((sx) => (
          <mesh
            key={`carriage-shuttle-rail-${sx}`}
            position={[sx * (halfW * 0.42), 0.14, carriageMotion.shuttleZ * 0.5]}
            material={mats.chromeShaft}
          >
            <boxGeometry args={[0.08, 0.08, depth * 0.75 + Math.abs(carriageMotion.shuttleZ)]} />
          </mesh>
        ))}

        {/* Motorized Shuttle Tray Nest (Translates along Z by carriageMotion.shuttleZ) */}
        <group position={[0, 0.18, carriageMotion.shuttleZ]} name="CarriageShuttleTrayNest">
          {/* Main Shuttle Plate */}
          <mesh material={mats.carriagePlate} castShadow receiveShadow>
            <boxGeometry args={[width * 0.68, 0.08, depth * 0.68]} />
          </mesh>
          {/* Linear Guide Bearings */}
          {[-1, 1].map((sx) => (
            <mesh
              key={`shuttle-bearing-${sx}`}
              position={[sx * (halfW * 0.42), -0.04, 0]}
              material={mats.darkChassis}
            >
              <boxGeometry args={[0.16, 0.12, 0.28]} />
            </mesh>
          ))}
          {/* Safety Yellow Cradle Arms */}
          {[-1, 1].map((sx) => (
            <group key={`cradle-arm-${sx}`} position={[sx * (halfW * 0.32), 0, 0]}>
              <mesh position={[0, 0.06, 0]} material={mats.safetyYellow} castShadow>
                <boxGeometry args={[0.08, 0.12, depth * 0.55]} />
              </mesh>
              {[-1, 1].map((fz) => (
                <mesh key={`finger-${fz}`} position={[sx * -0.04, 0.1, fz * (depth * 0.24)]} material={mats.safetyYellow} castShadow>
                  <boxGeometry args={[0.08, 0.08, 0.16]} />
                </mesh>
              ))}
            </group>
          ))}
          {/* SEMI Kinematic Locating Alignment Pins */}
          {[
            [0, 0.08, -depth * 0.2],
            [-width * 0.18, 0.08, depth * 0.16],
            [width * 0.18, 0.08, depth * 0.16],
          ].map(([px, py, pz], pIdx) => (
            <mesh key={`tray-pin-${pIdx}`} position={[px, py, pz]} material={mats.chromeShaft} castShadow>
              <cylinderGeometry args={[0.06, 0.08, 0.1, 12]} />
            </mesh>
          ))}
          {/* Status LED Perimeter Glow Edge */}
          <mesh position={[0, 0.04, halfD * 0.66]}>
            <boxGeometry args={[width * 0.66, 0.03, 0.03]} />
            <meshStandardMaterial color={activeColor} emissive={activeColor} emissiveIntensity={1.1} />
          </mesh>

          {/* Actual Semiconductor FOUP seated directly on the single moving tray */}
          {showFoup && carriageMotion.foupVisible && (
            <FOUP
              position={[0, 0.12, 0]}
              scale={foupScale}
            />
          )}
        </group>
      </group>

      {/* ── 10. Top Lifter Crown Frame (Capping Mast Columns at Y = effectiveTrackY) ── */}
      <group position={[0, effectiveTrackY, 0]}>
        {/* Left & Right Top Horizontal Perimeter Beams */}
        {[-1, 1].map((sx) => (
          <mesh
            key={`top-frame-lr-${sx}`}
            position={[sx * (halfW - postSize * 0.5), 0, 0]}
            material={mats.silverAluminum}
            castShadow
            receiveShadow
          >
            <boxGeometry args={[postSize, 0.28, depth]} />
          </mesh>
        ))}
        {/* Front & Back Top Horizontal Perimeter Beams */}
        {[-1, 1].map((sz) => (
          <mesh
            key={`top-frame-fb-${sz}`}
            position={[0, 0, sz * (halfD - postSize * 0.5)]}
            material={mats.silverAluminum}
            castShadow
            receiveShadow
          >
            <boxGeometry args={[width, 0.28, postSize]} />
          </mesh>
        ))}
        {/* 4 Corner Aluminum Column End Caps */}
        {[-1, 1].flatMap((sx) =>
          [-1, 1].map((sz) => (
            <mesh
              key={`top-cap-${sx}-${sz}`}
              position={[sx * (halfW - postSize * 0.5), 0.16, sz * (halfD - postSize * 0.5)]}
              material={mats.darkChassis}
              castShadow
            >
              <boxGeometry args={[postSize * 1.3, 0.08, postSize * 1.3]} />
            </mesh>
          ))
        )}
        {/* Top Motor Drive / Pulley Housing Enclosure */}
        {[-1, 1].map((sx) => (
          <group key={`top-motor-${sx}`} position={[sx * (halfW - 0.15), 0.22, 0]}>
            <mesh material={mats.darkChassis} castShadow>
              <boxGeometry args={[0.36, 0.32, 0.5]} />
            </mesh>
            <mesh position={[0, 0.18, 0]} material={mats.silverAluminum}>
              <cylinderGeometry args={[0.12, 0.12, 0.1, 16]} />
            </mesh>
          </group>
        ))}
      </group>
    </group>
  );
};

export default FabAmhsLifter3D;
